import { Close as CloseIcon } from '@mui/icons-material';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import type { FunctionComponent } from 'react';
import { useContext } from 'react';
import { useForm } from 'react-hook-form';

import { ApplicationContext } from '~/shared/context/context';
import type {
    SectionVisibilityBase,
    SectionVisibilityResponse,
    UserProfileVisibilityResponse,
    UserProfileVisibilityTemplateResponse,
} from '~/shared/data-contract/profile-visibility';

import { Headline, SubTitle } from '../../../../design/components/Typography';
import { getSectionVisibilityMessage, hasInitiallyAppliedSettingsCheck } from './helpers';
import { ProfileSectionAccordion } from './ProfileSectionAccordion';
import { useProfileVisibilityFormController } from './ProfileVisibilityFormController';
import { ResetConfigurationButton } from './ResetConfigurationButton';

export type ProfileSectionVisibilityKey =
    | 'ambitionsVisibility'
    | 'historyVisibility'
    | 'personalInfoVisibility'
    | 'skillsVisibility';

type Section = {
    readonly appliedSettings: SectionVisibilityResponse;
    readonly label: string;
    readonly minimumSettings: SectionVisibilityBase;
    readonly name: ProfileSectionVisibilityKey;
    readonly rolesList: readonly string[];
};

type ProfileVisibilityFormProps = {
    readonly data: UserProfileVisibilityResponse;
    readonly onClose: () => void;
    readonly refetch: () => void;
    readonly rolesList: readonly string[];
    readonly template: UserProfileVisibilityTemplateResponse;
};

export const ProfileVisibilityForm: FunctionComponent<ProfileVisibilityFormProps> = ({
    data,
    onClose,
    refetch,
    rolesList,
    template,
}) => {
    const { translate } = useContext(ApplicationContext);

    const {
        formState: { dirtyFields },
        getValues,
        setValue,
        watch,
    } = useForm<UserProfileVisibilityResponse>({
        values: data,
    });

    const enforceTemplate = (
        sectionKey: ProfileSectionVisibilityKey,
        data: SectionVisibilityResponse,
    ): SectionVisibilityResponse => {
        const minimum = template.minimum[sectionKey];
        const everyone = data.everyone || minimum.everyone;
        const roles = everyone ? [] : [...new Set([...data.roles, ...minimum.roles])];
        const users = everyone ? [] : data.users;

        return {
            everyone,
            roles,
            users,
        };
    };

    const setSectionValue = (sectionKey: ProfileSectionVisibilityKey, data: SectionVisibilityResponse): void => {
        setValue(sectionKey, enforceTemplate(sectionKey, data), { shouldDirty: true });
    };

    const getSectionValue = (sectionKey: ProfileSectionVisibilityKey): SectionVisibilityResponse =>
        getValues(sectionKey);

    const { onEveryoneClick, onResetConfiguration, onResetSection, onRoleClick, onSaveSection, onUserClick } =
        useProfileVisibilityFormController({
            getSectionValue,
            refetch,
            setSectionValue,
        });

    const hasInitiallyAppliedSettings = hasInitiallyAppliedSettingsCheck(data, template.minimum);

    const watchedAmbitionsVisibility = watch('ambitionsVisibility');
    const watchedHistoryVisibility = watch('historyVisibility');
    const watchedSkillsVisibility = watch('skillsVisibility');
    const watchedPersonalInfoVisibility = watch('personalInfoVisibility');

    const sections: readonly Section[] = [
        {
            appliedSettings: watchedSkillsVisibility,
            label: translate('pages.user-profile.profile-visibility.drawer.apply-settings-to.skills-label'),
            minimumSettings: template.minimum.skillsVisibility,
            name: 'skillsVisibility',
            rolesList,
        },
        {
            appliedSettings: watchedAmbitionsVisibility,
            label: translate('pages.user-profile.profile-visibility.drawer.apply-settings-to.ambitions-label'),
            minimumSettings: template.minimum.ambitionsVisibility,
            name: 'ambitionsVisibility',
            rolesList,
        },
        {
            appliedSettings: watchedHistoryVisibility,
            label: translate('pages.user-profile.profile-visibility.drawer.apply-settings-to.profile-history-label'),
            minimumSettings: template.minimum.historyVisibility,
            name: 'historyVisibility',
            rolesList,
        },
        {
            appliedSettings: watchedPersonalInfoVisibility,
            label: translate(
                'pages.user-profile.profile-visibility.drawer.apply-settings-to.personal-information-label',
            ),
            minimumSettings: template.minimum.personalInfoVisibility,
            name: 'personalInfoVisibility',
            rolesList,
        },
    ];

    return (
        <form
            style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}
            onSubmit={(event) => event.preventDefault()}
        >
            <Grid>
                <Grid alignItems="center" container={true} justifyContent="space-between" minHeight={64} paddingX={3}>
                    <Headline variant="h5">{translate('pages.user-profile.profile-visibility.drawer.title')}</Headline>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Grid>
                <Divider />
                <Grid mx={3} my={2}>
                    <Headline variant="h5">
                        {translate('pages.user-profile.profile-visibility.drawer.sections.headline')}
                    </Headline>
                    <Grid container={true} gap={1}>
                        <SubTitle>
                            {translate('pages.user-profile.profile-visibility.drawer.sections.subtitle')}
                        </SubTitle>
                    </Grid>
                </Grid>
                <Grid mx={3} my={2}>
                    <Container
                        sx={{
                            backgroundColor: '#FCE2B6',
                            borderRadius: 2,
                            px: 3,
                            py: 2,
                        }}
                    >
                        <Typography>
                            <Typography component="span" sx={{ fontWeight: 700 }} variant="subtitle2">
                                {translate('pages.user-profile.profile-visibility.drawer.information-box.please-note')}
                            </Typography>{' '}
                            <Typography
                                component="span"
                                // biome-ignore lint/security/noDangerouslySetInnerHtml: suppressed due to migration
                                dangerouslySetInnerHTML={{
                                    __html: translate(
                                        'pages.user-profile.profile-visibility.drawer.information-box.text',
                                    ),
                                }}
                                variant="body2"
                            />
                        </Typography>
                    </Container>
                </Grid>
                {sections.map(({ appliedSettings, label, minimumSettings, name, rolesList }) => {
                    const { everyone, roles, users } = data[name];
                    const isSectionDirty = Boolean(dirtyFields[name]);

                    return (
                        <ProfileSectionAccordion
                            key={name}
                            appliedSettings={appliedSettings}
                            isSectionDirty={isSectionDirty}
                            minimumSettings={minimumSettings}
                            rolesList={rolesList}
                            sectionLabel={label}
                            visibilityMessage={translate(
                                getSectionVisibilityMessage({
                                    everyone,
                                    roles,
                                    users,
                                }),
                            )}
                            onEveryoneClick={onEveryoneClick(name)}
                            onResetSection={onResetSection(name)}
                            onRoleClick={onRoleClick(name)}
                            onSaveSection={onSaveSection(name)}
                            onUserClick={onUserClick(name)}
                        />
                    );
                })}
            </Grid>
            <Grid alignItems="center" container={true} justifyContent="space-between" minHeight={64} paddingX={3}>
                <ResetConfigurationButton
                    disabled={hasInitiallyAppliedSettings}
                    onResetConfiguration={onResetConfiguration}
                />
            </Grid>
        </form>
    );
};
